import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import CtaForm from '@/components/CtaForm';
import { useParallaxRef } from '@/hooks/useParallax';

const FAQ_ITEMS = [
  {
    question: 'How do you evaluate offers?',
    answer:
      'We consider offer amount, intended use case, timeline, and overall project credibility. Strong business plans often compensate for lower initial offers.',
  },
  {
    question: "What's the typical response time?",
    answer:
      'We respond to all offers within 24-48 hours with either acceptance, counter-offer, or detailed feedback on rejection.',
  },
  {
    question: 'How can I increase acceptance odds?',
    answer:
      'Include specific use case details, realistic timeline, and offer within 70-90% of BIN price. Demonstrate serious intent and business viability.',
  },
  {
    question: 'Is my offer information confidential?',
    answer:
      'Yes, all offer details and business plans are kept strictly confidential and never shared with third parties.',
  },
];

export default function ApplyOffer() {
  const [searchParams] = useSearchParams();
  const domain = searchParams.get('domain');
  const [submitted, setSubmitted] = useState(false);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 60 });

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
        <div className="max-w-2xl mx-auto stagger-fade-1">
          <Card className="apple-card animate-fade-in-up">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h1 className="text-3xl font-bold mb-4">Offer Submitted!</h1>
              <p className="text-muted-foreground mb-6">
                Thanks for your offer on <strong>{domain}</strong>. We'll review it carefully and
                respond within 24-48 hours.
              </p>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  <strong>Next steps:</strong> Our team will evaluate your offer against current
                  market conditions and respond with acceptance, counter-offer, or detailed
                  feedback.
                </p>
                <Link to="/domains">
                  <Button variant="outline" className="apple-button-secondary-cta">
                    Browse More Domains
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="mb-4 stagger-fade-1">
          <Link
            to={domain ? `/domain/${domain}` : '/domains'}
            className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {domain ? `Back to ${domain}` : 'Back to Domains'}
          </Link>
        </div>

        {/* Header */}
        <Card className="apple-card stagger-fade-1 animate-fade-in-up">
          <CardHeader className="pb-4">
            <CardTitle
              ref={heroTitleRef as React.RefObject<HTMLHeadingElement>}
              className="text-3xl font-bold"
            >
              Make an offer for {domain}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <p className="text-muted-foreground">
                I review all offers carefully and respond quickly. Strong business cases often
                matter more than price alone.
              </p>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-primary">Faster for Reasonable Offers</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Offers within 70-90% of BIN typically get priority review and faster escrow
                      setup.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offer FAQ */}
        <Card className="apple-card stagger-fade-2 animate-fade-in-up">
          <CardHeader>
            <CardTitle>Offer Process</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {FAQ_ITEMS.map((item, index) => (
              <details key={index} className="group">
                <summary className="flex items-center justify-between cursor-pointer p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <span className="font-medium">{item.question}</span>
                  <span className="text-muted-foreground group-open:rotate-180 transition-transform">
                    ▼
                  </span>
                </summary>
                <div className="px-3 py-2 text-sm text-muted-foreground">{item.answer}</div>
              </details>
            ))}
          </CardContent>
        </Card>

        {/* Offer Form */}
        <Card className="apple-card stagger-fade-3 animate-fade-in-up">
          <CardHeader>
            <CardTitle>Submit Your Offer</CardTitle>
          </CardHeader>
          <CardContent>
            <CtaForm
              pageType="offer"
              subjectPrefix="[MAKE OFFER]"
              onSuccess={() => setSubmitted(true)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
